import { useEffect, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Loader2,
  Package,
  Pencil,
  Plus,
  Trash2,
  X,
  XCircle,
} from 'lucide-react';
import { api, extractErrorMessage } from '../../lib/api';
import { ModalPortal } from '../ModalPortal';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';

/**
 * Panneau "Pièces utilisées" du drawer Intervention (US-INT-06).
 *
 * CRUD des pièces consommées + workflow de validation (US client) : une pièce
 * ajoutée par un prestataire reste « en attente » jusqu'à validation par le
 * biomédical ; seules les pièces validées comptent dans le total.
 */

type StatutValidation = 'validee' | 'en_attente' | 'refusee';

interface PieceRow {
  id: string;
  intervention_id: string;
  designation: string;
  reference: string | null;
  quantite: number;
  cout_unitaire: number | null;
  cout_total: number | null;
  date_utilisation: string | null;
  statut_validation: StatutValidation;
  motif_refus: string | null;
  created_at: string;
}

/** Badge de statut de validation d'une pièce. */
function PieceStatutBadge({ statut }: { statut: StatutValidation }) {
  const cfg: Record<StatutValidation, { label: string; cls: string; Icon: typeof CheckCircle2 }> = {
    validee: {
      label: 'Validée',
      cls: 'bg-green-100 text-green-800 border-green-200',
      Icon: CheckCircle2,
    },
    en_attente: {
      label: 'En attente',
      cls: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      Icon: Clock,
    },
    refusee: { label: 'Refusée', cls: 'bg-red-100 text-red-800 border-red-200', Icon: XCircle },
  };
  const { label, cls, Icon } = cfg[statut];
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded font-medium border ${cls}`}
    >
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

export function PiecesPanel({
  interventionId,
  onRefresh,
}: {
  interventionId: string;
  onRefresh?(): void;
}) {
  const { user } = useAuth();
  const { can } = usePermissions();
  const roleNames = user?.role_names ?? [];
  // Biomédical / admin (ou super-admin) : peut valider/refuser les pièces.
  const canValidate =
    !!user?.is_platform_admin ||
    roleNames.includes('Administrateur tenant') ||
    roleNames.includes('Responsable biomédical');
  // Peut ajouter/éditer des pièces (biomédical, admin, prestataire).
  const canEdit = can('interventions', 'update');

  const [items, setItems] = useState<PieceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [editRow, setEditRow] = useState<PieceRow | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [validatingId, setValidatingId] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      const res = await api.get<{ data: PieceRow[] }>(`/interventions/${interventionId}/pieces`);
      setItems(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interventionId]);

  async function handleDelete(p: PieceRow) {
    if (!confirm(`Supprimer la pièce "${p.designation}" ?`)) return;
    try {
      await api.delete(`/interventions/pieces/${p.id}`);
      refresh();
      onRefresh?.();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }

  async function handleValidate(p: PieceRow, approve: boolean) {
    const motif = approve ? undefined : (prompt('Motif du refus (optionnel) :') ?? undefined);
    setValidatingId(p.id);
    setErrorMessage(null);
    try {
      await api.post(`/interventions/pieces/${p.id}/valider`, { approve, motif });
      refresh();
      onRefresh?.();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setValidatingId(null);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
      </div>
    );
  }

  // Seules les pièces VALIDÉES comptent dans le total (aligné sur le back).
  const totalPieces = items
    .filter((it) => it.statut_validation === 'validee')
    .reduce((acc, it) => acc + (it.cout_total ?? 0), 0);
  const nbEnAttente = items.filter((it) => it.statut_validation === 'en_attente').length;

  return (
    <div className="space-y-3">
      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {canEdit && (
        <div className="flex items-center justify-between gap-2">
          {nbEnAttente > 0 && (
            <span className="inline-flex items-center gap-1 text-xs text-yellow-800">
              <Clock className="w-3.5 h-3.5" />
              {nbEnAttente} pièce{nbEnAttente > 1 ? 's' : ''} en attente de validation
            </span>
          )}
          <button
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-lg border border-primary-200 ml-auto"
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            Ajouter une pièce
          </button>
        </div>
      )}

      {items.length === 0 ? (
        <div className="text-center py-8 text-sm text-gray-500">
          <Package className="w-8 h-8 mx-auto text-gray-300 mb-2" />
          Aucune pièce utilisée enregistrée.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Désignation
                </th>
                <th className="hidden md:table-cell px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Référence
                </th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Qté
                </th>
                <th className="hidden md:table-cell px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  PU
                </th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="hidden lg:table-cell px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-28" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {items.map((p) => (
                <tr key={p.id}>
                  <td className="px-3 py-2 text-gray-900">{p.designation}</td>
                  <td className="hidden md:table-cell px-3 py-2 text-gray-600">
                    {p.reference ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-700">{p.quantite}</td>
                  <td className="hidden md:table-cell px-3 py-2 text-right text-gray-700">
                    {p.cout_unitaire !== null ? p.cout_unitaire.toFixed(2) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-900 font-medium">
                    {p.cout_total !== null ? p.cout_total.toFixed(2) : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <PieceStatutBadge statut={p.statut_validation} />
                    {p.statut_validation === 'refusee' && p.motif_refus && (
                      <span className="block text-xs text-red-600 mt-0.5">{p.motif_refus}</span>
                    )}
                  </td>
                  <td className="hidden lg:table-cell px-3 py-2 text-gray-600">
                    {p.date_utilisation ? new Date(p.date_utilisation).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center space-x-1">
                      {canValidate && p.statut_validation === 'en_attente' && (
                        <>
                          <button
                            onClick={() => handleValidate(p, true)}
                            disabled={validatingId === p.id}
                            className="p-1 rounded hover:bg-green-50 text-green-600 disabled:opacity-50"
                            title="Valider la pièce"
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleValidate(p, false)}
                            disabled={validatingId === p.id}
                            className="p-1 rounded hover:bg-red-50 text-red-600 disabled:opacity-50"
                            title="Refuser la pièce"
                          >
                            <XCircle className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                      {canEdit && (
                        <button
                          onClick={() => setEditRow(p)}
                          className="p-1 rounded hover:bg-gray-100 text-gray-600"
                          title="Modifier"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {canEdit && (
                        <button
                          onClick={() => handleDelete(p)}
                          className="p-1 rounded hover:bg-red-50 text-red-600"
                          title="Supprimer"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr>
                <td colSpan={4} className="px-3 py-2 text-right text-xs font-medium text-gray-600">
                  Total pièces validées
                </td>
                <td className="px-3 py-2 text-right text-gray-900 font-bold">
                  {totalPieces.toFixed(2)}
                </td>
                <td colSpan={3} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {createOpen && (
        <PieceFormModal
          interventionId={interventionId}
          onClose={() => setCreateOpen(false)}
          onSaved={() => {
            setCreateOpen(false);
            refresh();
            onRefresh?.();
          }}
        />
      )}

      {editRow && (
        <PieceFormModal
          interventionId={interventionId}
          piece={editRow}
          onClose={() => setEditRow(null)}
          onSaved={() => {
            setEditRow(null);
            refresh();
            onRefresh?.();
          }}
        />
      )}
    </div>
  );
}

function PieceFormModal({
  interventionId,
  piece,
  onClose,
  onSaved,
}: {
  interventionId: string;
  piece?: PieceRow;
  onClose(): void;
  onSaved(): void;
}) {
  const isEdit = !!piece;
  const [designation, setDesignation] = useState(piece?.designation ?? '');
  const [reference, setReference] = useState(piece?.reference ?? '');
  const [quantite, setQuantite] = useState(String(piece?.quantite ?? 1));
  const [coutUnitaire, setCoutUnitaire] = useState(
    piece?.cout_unitaire !== null && piece?.cout_unitaire !== undefined
      ? String(piece.cout_unitaire)
      : '',
  );
  const [dateUtilisation, setDateUtilisation] = useState(piece?.date_utilisation ?? '');
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!designation.trim()) {
      setErrorMessage('La désignation est obligatoire.');
      return;
    }
    setSaving(true);
    setErrorMessage(null);
    try {
      const payload = {
        designation: designation.trim(),
        reference: reference.trim() || null,
        quantite: Number(quantite),
        cout_unitaire: coutUnitaire === '' ? null : Number(coutUnitaire),
        date_utilisation: dateUtilisation || null,
      };
      if (isEdit && piece) {
        await api.patch(`/interventions/pieces/${piece.id}`, payload);
      } else {
        await api.post(`/interventions/${interventionId}/pieces`, payload);
      }
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Package className="w-4 h-4 mr-2 text-primary-600" />
              {isEdit ? 'Modifier la pièce' : 'Ajouter une pièce'}
            </h3>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <form onSubmit={submit} className="p-4 space-y-3">
            {errorMessage && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Désignation *</label>
              <input
                value={designation}
                onChange={(e) => setDesignation(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Référence</label>
                <input
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Quantité *</label>
                <input
                  type="number"
                  min={0.01}
                  step="any"
                  value={quantite}
                  onChange={(e) => setQuantite(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Coût unitaire
                </label>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={coutUnitaire}
                  onChange={(e) => setCoutUnitaire(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Date d'utilisation
                </label>
                <input
                  type="date"
                  value={dateUtilisation ? dateUtilisation.slice(0, 10) : ''}
                  onChange={(e) => setDateUtilisation(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center px-4 py-2 rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {isEdit ? 'Enregistrer' : 'Ajouter'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </ModalPortal>
  );
}
